/**
 * ToughBarPreviewModal.js
 * Preview modal system for Tough-Bar Builder with Swiper support and metafields
 * Based on MountBuilderPreviewModal pattern
 */

class ToughBarPreviewModal {
    constructor() {
        this.activeModal = null;
        this.productCache = new Map();
        this.swiperInstances = {};
        this.metafieldCache = new Map();
        this.closeCallbacks = {};

        // Color configuration matching tough-bar builder theme
        this.colorConfig = {
            primary: '#3D5A80',
            primaryHover: '#324a6b',
            primaryLight: '#E7EEF5',
            primaryText: '#3D5A80',
            accent: '#0A0C0E',
            bulletActive: '#3D5A80',
            bulletInactive: '#949499',
            borderActive: '#3D5A80',
            featuresMarker: '#3D5A80',
            danger: '#E3371E'
        };

        this._addStyles();
        this._addSwiperStyles();
        this._addColorVariables();
        this._setupGlobalEventHandlers();
    }

    /**
     * Setup global event handlers
     */
    _setupGlobalEventHandlers() {
        // Handle modal close events
        document.addEventListener('click', (e) => {
            const closeBtn = e.target.closest('.modal-close, [data-backdrop="true"]');
            if (closeBtn && closeBtn.closest('.tough-bar-preview-modal')) {
                e.preventDefault();
                e.stopPropagation();
                this.close();
            }
        });

        // Handle ESC key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.activeModal) {
                this.close();
            }
        });
    }

    /**
     * Adds CSS variables for color theming
     */
    _addColorVariables() {
        if (document.getElementById('tough-bar-preview-color-variables')) return;

        const style = document.createElement('style');
        style.id = 'tough-bar-preview-color-variables';
        style.textContent = `
            .tough-bar-preview-modal {
                --tb-primary: ${this.colorConfig.primary};
                --tb-primary-hover: ${this.colorConfig.primaryHover};
                --tb-primary-light: ${this.colorConfig.primaryLight};
                --tb-primary-text: ${this.colorConfig.primaryText};
                --tb-accent: ${this.colorConfig.accent};
                --tb-bullet-active: ${this.colorConfig.bulletActive};
                --tb-bullet-inactive: ${this.colorConfig.bulletInactive};
                --tb-border-active: ${this.colorConfig.borderActive};
                --tb-features-marker: ${this.colorConfig.featuresMarker};
                --tb-danger: ${this.colorConfig.danger};
            }
        `;
        document.head.appendChild(style);
    }

    /**
     * Fetches variant metafields from the backend
     * Uses zebra-builder endpoint (shared metafields service)
     */
    async fetchVariantMetafields(variantId) {
        if (this.metafieldCache.has(variantId)) {
            return this.metafieldCache.get(variantId);
        }

        try {
            const targetEndpoint = 'variant-metafields-v1';
            const response = await fetch(`/apps/zebra-builder?target=${encodeURIComponent(targetEndpoint)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Target-Endpoint': targetEndpoint
                },
                body: JSON.stringify({
                    variant_id: `gid://shopify/ProductVariant/${variantId}`
                })
            });

            if (!response.ok) {
                console.warn('[ToughBarPreviewModal] Failed to fetch variant metafields:', response.status);
                return null;
            }

            const data = await response.json();
            this.metafieldCache.set(variantId, data);
            return data;
        } catch (error) {
            console.error('[ToughBarPreviewModal] Error fetching variant metafields:', error);
            return null;
        }
    }

    /**
     * Opens preview modal for a product
     */
    async open(productHandle, triggerElement = null, options = {}) {
        const modalOptions = (options && typeof options === 'object') ? options : {};
        this.closeCallbacks = {
            onAfterClose: typeof modalOptions.onAfterClose === 'function' ? modalOptions.onAfterClose : null
        };

        if (triggerElement) {
            this._showElementLoading(triggerElement, true);
        }

        try {
            const product = await this.fetchProductData(productHandle);
            if (!product) throw new Error("Product data could not be loaded.");

            const variantId = product.variants?.[0]?.id;
            let variantMetafields = null;
            if (variantId) {
                try {
                    variantMetafields = await this.fetchVariantMetafields(variantId);
                } catch (e) {
                    console.warn('[ToughBarPreviewModal] Could not fetch variant metafields:', e);
                }
            }

            document.querySelector('.tough-bar-preview-modal')?.remove();

            const modalHTML = this._generateHTML(product, variantMetafields);
            document.body.insertAdjacentHTML('beforeend', modalHTML);

            const modal = document.querySelector('.tough-bar-preview-modal');
            this.activeModal = modal;

            this._setupModal(modal);
            this._lockScroll();
            return true;

        } catch (error) {
            console.error('[ToughBarPreviewModal] Error opening preview modal:', error);
            this.closeCallbacks = {};
            alert('Failed to load product preview');
            return false;
        } finally {
            if (triggerElement) {
                this._showElementLoading(triggerElement, false);
            }
        }
    }

    /**
     * Fetches product data with caching
     */
    async fetchProductData(handle) {
        if (this.productCache.has(handle)) {
            return this.productCache.get(handle);
        }

        try {
            const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`);
            if (!response.ok) return null;

            const productData = await response.json();
            this.productCache.set(handle, productData);
            return productData;
        } catch (error) {
            console.error(`[ToughBarPreviewModal] Error fetching product ${handle}:`, error);
            return null;
        }
    }

    /**
     * Sets up modal animations and interactions
     */
    _setupModal(modal) {
        const backdrop = modal.querySelector('.preview-modal-backdrop');
        const content = modal.querySelector('.preview-modal-content');
        const mobileContent = modal.querySelector('.mobile-preview-content');

        void modal.offsetHeight;

        modal.style.opacity = '1';
        modal.style.pointerEvents = 'auto';
        if (backdrop) backdrop.style.opacity = '1';

        if (window.innerWidth >= 768 && content) {
            content.style.opacity = '1';
            content.style.transform = 'scale(1)';
        } else if (mobileContent) {
            mobileContent.style.opacity = '1';
            mobileContent.style.transform = 'translateY(0)';
            this._setupSwipeToClose(modal, mobileContent);
        }

        // Initialize Swiper if available
        if (typeof Swiper !== 'undefined') {
            this._initializeSwiper(modal);
        }

        // Setup video play button handlers
        this._setupVideoHandlers(modal);
    }

    /**
     * Setup click handlers for video play buttons
     */
    _setupVideoHandlers(modal) {
        const playButtons = modal.querySelectorAll('.tb-video-play-btn');

        playButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                const embedUrl = btn.dataset.embedUrl;
                const container = btn.closest('.tb-video-container');

                if (!container || !embedUrl) return;

                // Replace poster + play button with iframe
                const iframe = document.createElement('iframe');
                iframe.src = embedUrl + '&autoplay=1';
                iframe.className = 'tb-video-iframe';
                iframe.setAttribute('frameborder', '0');
                iframe.setAttribute('allowfullscreen', '');
                iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');

                container.innerHTML = '';
                container.appendChild(iframe);
            });
        });
    }

    /**
     * Initialize Swiper galleries
     */
    _initializeSwiper(modal) {
        const isMobile = window.innerWidth < 768;
        const mainSelector = isMobile ? '.preview-main-gallery-mobile' : '.preview-main-gallery';
        const thumbsSelector = isMobile ? '.preview-thumbnails-gallery-mobile' : '.preview-thumbnails-gallery';

        const mainEl = modal.querySelector(mainSelector);
        const thumbsEl = modal.querySelector(thumbsSelector);

        if (!mainEl) return;

        const hasMultipleImages = mainEl.querySelectorAll('.swiper-slide').length > 1;
        if (!hasMultipleImages) return;

        let thumbsSwiper = null;
        if (thumbsEl) {
            thumbsSwiper = new Swiper(thumbsEl, {
                spaceBetween: 8,
                slidesPerView: 'auto',
                freeMode: true,
                watchSlidesProgress: true,
                allowTouchMove: true,
                navigation: {
                    nextEl: thumbsEl.querySelector('.thumbnail-button-next'),
                    prevEl: thumbsEl.querySelector('.thumbnail-button-prev'),
                },
            });
            this.swiperInstances.thumbs = thumbsSwiper;
        }

        const mainSwiper = new Swiper(mainEl, {
            effect: 'fade',
            fadeEffect: { crossFade: true },
            allowTouchMove: true,
            loop: true,
            grabCursor: true,
            preventClicks: true,
            preventClicksPropagation: true,
            pagination: isMobile ? {
                el: mainEl.querySelector('.preview-pagination-mobile'),
                clickable: true
            } : false,
            navigation: {
                nextEl: mainEl.querySelector('.preview-button-next'),
                prevEl: mainEl.querySelector('.preview-button-prev'),
            },
            thumbs: thumbsSwiper ? { swiper: thumbsSwiper } : undefined,
        });
        this.swiperInstances.main = mainSwiper;
    }

    /**
     * Setup swipe to close for mobile
     */
    _setupSwipeToClose(modal, mobileContent) {
        const dragHandle = mobileContent.querySelector('.drag-handle-area');
        const imageArea = mobileContent.querySelector('.image-container');
        const scrollableContent = mobileContent.querySelector('.overflow-y-auto');

        let startY = 0;
        let currentY = 0;
        let isDragging = false;
        let startTime = 0;

        const onTouchStart = (e) => {
            const isAtTop = scrollableContent ? scrollableContent.scrollTop === 0 : true;
            const isDragArea = e.target.closest('.drag-handle-area') || e.target.closest('.image-container');

            if (!isDragArea && !isAtTop) return;

            startY = e.touches[0].clientY;
            currentY = startY;
            startTime = Date.now();
            isDragging = true;
            mobileContent.style.transition = 'none';
        };

        const onTouchMove = (e) => {
            if (!isDragging) return;

            currentY = e.touches[0].clientY;
            const deltaY = currentY - startY;

            if (deltaY > 0) {
                e.preventDefault();
                mobileContent.style.transform = `translateY(${deltaY}px)`;
            }
        };

        const onTouchEnd = () => {
            if (!isDragging) return;
            isDragging = false;

            const deltaY = currentY - startY;
            const deltaTime = Date.now() - startTime;
            const velocity = deltaTime > 0 ? deltaY / deltaTime : 0;

            const shouldClose = deltaY > (window.innerHeight / 4) ||
                                  (velocity > 0.5 && deltaY > 50);

            mobileContent.style.transition = 'transform 0.3s ease-out';

            if (shouldClose) {
                this.close();
            } else {
                mobileContent.style.transform = 'translateY(0)';
            }
        };

        [dragHandle, imageArea].forEach(el => {
            if (el) {
                el.addEventListener('touchstart', onTouchStart, { passive: true });
            }
        });

        mobileContent.addEventListener('touchmove', onTouchMove, { passive: false });
        mobileContent.addEventListener('touchend', onTouchEnd);
        mobileContent.addEventListener('touchcancel', onTouchEnd);
    }

    /**
     * Closes the modal
     */
    close() {
        if (!this.activeModal) return;

        Object.values(this.swiperInstances).forEach(swiper => swiper?.destroy(true, true));
        this.swiperInstances = {};

        const modal = this.activeModal;
        const closeCallbacks = this.closeCallbacks || {};
        const backdrop = modal.querySelector('.preview-modal-backdrop');
        const content = modal.querySelector('.preview-modal-content');
        const mobileContent = modal.querySelector('.mobile-preview-content');

        modal.style.opacity = '0';
        modal.style.pointerEvents = 'none';
        if (backdrop) backdrop.style.opacity = '0';

        if (window.innerWidth >= 768 && content) {
            content.style.transform = 'scale(0.95)';
        } else if (mobileContent) {
            mobileContent.style.transform = 'translateY(100%)';
        }

        setTimeout(() => {
            modal.remove();
            this.activeModal = null;
            this._unlockScroll();
            this.closeCallbacks = {};
            if (typeof closeCallbacks.onAfterClose === 'function') {
                closeCallbacks.onAfterClose();
            }
        }, 300);
    }

    /**
     * Get metafield value helper
     */
    _getMetafieldValue(metafields, key) {
        if (!metafields || !metafields.metafields) return '';
        const metafield = metafields.metafields.find(mf =>
            mf.namespace === 'variant_attrs' && mf.key === key
        );
        return metafield ? metafield.value : '';
    }

    /**
     * Parse video data from metafields
     * Returns array of video objects: [{ platform: 'youtube', id: 'abc123' }, ...]
     */
    _getVideoData(variantMetafields) {
        const videoMetafield = this._getMetafieldValue(variantMetafields, 'product_video');
        if (!videoMetafield) return [];

        try {
            const videoData = JSON.parse(videoMetafield);
            const videos = [];

            // Parse platform_1/id_1, platform_2/id_2, etc.
            let index = 1;
            while (videoData[`platform_${index}`] && videoData[`id_${index}`]) {
                const platform = videoData[`platform_${index}`];
                const id = videoData[`id_${index}`];

                if (platform === 'youtube' && id) {
                    videos.push({
                        platform: 'youtube',
                        id: id.trim(),
                        embedUrl: `https://www.youtube-nocookie.com/embed/${id.trim()}?rel=0&modestbranding=1&playsinline=1&iv_load_policy=3&dnt=1`,
                        thumbUrl: `https://i.ytimg.com/vi/${id.trim()}/hqdefault.jpg`
                    });
                }
                index++;
            }

            return videos;
        } catch (e) {
            console.warn('[ToughBarPreviewModal] Error parsing video data:', e);
            return [];
        }
    }

    /**
     * Clean description HTML - preserves line breaks
     */
    _cleanOverviewText(text) {
        if (!text) return '';
        let cleaned = text
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/p>/gi, '\n\n')
            .replace(/<[^>]*>/g, '');
        cleaned = cleaned.replace(/^\s+|\s+$/g, '');
        return cleaned.replace(/\n/g, '<br>');
    }

    /**
     * Format price for display
     */
    _formatPrice(price) {
        if (!price) return '';
        const priceInDollars = (parseInt(price) / 100).toFixed(2);
        return `$${priceInDollars}`;
    }

    /**
     * Generate price HTML with compare-at styling and product link
     */
    _generatePriceHTML(variant, productHandle) {
        const productUrl = `${window.Shopify?.routes?.root || '/'}products/${productHandle}`;

        // Generate price section only if price exists
        let priceSection = '';
        if (variant && variant.price) {
            const hasComparePrice = variant.compare_at_price &&
                                   parseInt(variant.compare_at_price) > parseInt(variant.price);

            priceSection = hasComparePrice ? `
                <div class="flex items-center">
                    <span class="font-medium">Price:</span>
                    <span class="ml-1 font-light tb-price-compare">
                        ${this._formatPrice(variant.compare_at_price)}
                    </span>
                    <span class="ml-2 font-semibold tb-price-sale">
                        ${this._formatPrice(variant.price)}
                    </span>
                </div>
            ` : `
                <div class="flex items-center">
                    <span class="font-medium">Price:</span>
                    <span class="ml-1 font-light">${this._formatPrice(variant.price)}</span>
                </div>
            `;
        }

        // Always return the product link, with price section if available
        return `
            ${priceSection}
            <div class="mt-2">
                <a href="${productUrl}"
                   target="_blank"
                   rel="noopener noreferrer"
                   class="tb-product-link inline-flex items-center text-sm font-medium">
                    View Full Product Details
                    <svg class="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                    </svg>
                </a>
            </div>
        `;
    }

    /**
     * Generate overview content with truncation and see more/less
     */
    _generateOverviewContent(overview) {
        if (!overview) return '';
        const cleanOverview = this._cleanOverviewText(overview);
        const averageCharsPerLine = 80;
        const maxLines = 3;
        const lines = Math.ceil(cleanOverview.replace(/<br>/g, '').length / averageCharsPerLine);

        if (lines <= maxLines) {
            return `
                <div class="mt-4">
                    <h4 class="text-sm font-medium text-gray-900 mb-2">Overview</h4>
                    <div class="text-sm text-gray-600 leading-relaxed">${cleanOverview}</div>
                </div>
            `;
        } else {
            const truncatedLength = Math.floor(maxLines * averageCharsPerLine);
            const truncatedText = cleanOverview.substring(0, truncatedLength);
            const finalTruncated = truncatedText.substring(0, truncatedText.lastIndexOf(' '));
            const overviewId = `overview-${Math.random().toString(36).substring(2, 9)}`;

            return `
                <div class="mt-4">
                    <h4 class="text-sm font-medium text-gray-900 mb-2">Overview</h4>
                    <div class="text-sm text-gray-600 leading-relaxed">
                        <div id="${overviewId}-truncated">
                            ${finalTruncated}...
                            <button type="button" class="tb-see-more-btn"
                                onclick="this.parentElement.style.display='none'; this.parentElement.nextElementSibling.style.display='block';">
                                See more
                            </button>
                        </div>
                        <div id="${overviewId}-full" style="display: none;">
                            ${cleanOverview}
                            <button type="button" class="tb-see-less-btn"
                                onclick="this.parentElement.style.display='none'; this.parentElement.previousElementSibling.style.display='block';">
                                See less
                            </button>
                        </div>
                    </div>
                </div>
            `;
        }
    }

    /**
     * Generate features content with styled bullets
     */
    _generateFeaturesContent(variantMetafields) {
        const featuresMetafield = this._getMetafieldValue(variantMetafields, 'features');
        if (!featuresMetafield) return '';

        try {
            const featuresData = JSON.parse(featuresMetafield);
            const featuresArray = Object.keys(featuresData)
                .filter(key => key.startsWith('feature_') && featuresData[key])
                .map(key => featuresData[key]);

            if (featuresArray.length === 0) return '';

            const featuresHtml = featuresArray.map(feature =>
                `<li class="flex items-start text-sm text-gray-600 leading-relaxed">
                    <span class="tb-features-marker mr-2 mt-1">•</span>
                    <span>${feature}</span>
                </li>`
            ).join('');

            return `
                <div class="mt-4 pt-4">
                    <h4 class="text-sm font-medium text-gray-900 mb-3">Features</h4>
                    <ul class="space-y-2">${featuresHtml}</ul>
                </div>
            `;
        } catch (e) {
            console.warn('[ToughBarPreviewModal] Error parsing features:', e);
            return '';
        }
    }

    /**
     * Generate specifications content with collapsible section
     */
    _generateSpecsContent(variantMetafields) {
        const specsMetafield = this._getMetafieldValue(variantMetafields, 'specs');
        if (!specsMetafield) return '';

        try {
            const specsData = JSON.parse(specsMetafield);
            const specsEntries = Object.entries(specsData);
            if (specsEntries.length === 0) return '';

            const formatLabel = (key) => key
                .replace(/_/g, ' ')
                .replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/\b\w/g, l => l.toUpperCase());

            const specsHtml = specsEntries.map(([key, value]) =>
                `<div style="background-color: #F9FAFB; border-radius: 0.5rem; padding: 0.75rem;">
                    <dt class="text-sm font-medium text-gray-700 mb-1">${formatLabel(key)}</dt>
                    <dd class="text-sm text-gray-600">${String(value).replace(/\|/g, '<br>')}</dd>
                </div>`
            ).join('');

            return `
                <div class="mt-4 pt-4">
                    <button type="button" class="w-full flex items-center justify-between text-left"
                        onclick="this.closest('div').querySelector('.specs-content').classList.toggle('hidden');
                                 this.querySelector('.specs-toggle-icon').classList.toggle('rotate-45');">
                        <h4 class="text-sm font-medium text-gray-900">Specifications</h4>
                        <svg class="w-4 h-4 text-gray-600 transition-transform duration-200 specs-toggle-icon rotate-45"
                            fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                        </svg>
                    </button>
                    <div class="specs-content mt-3 space-y-3">
                        ${specsHtml}
                    </div>
                </div>
            `;
        } catch (e) {
            console.warn('[ToughBarPreviewModal] Error parsing specs:', e);
            return '';
        }
    }

    /**
     * Generate modal HTML with Swiper support
     */
    _generateHTML(product, variantMetafields = null) {
        const variant = product.variants?.[0] || {};
        const available = variant.available || false;
        const images = product.images || [];
        const videos = this._getVideoData(variantMetafields);
        const totalMedia = images.length + videos.length;
        const hasMultipleMedia = totalMedia > 1;
        const hasMoreThanFive = totalMedia > 5;

        // Get metafield data
        const overview = this._getMetafieldValue(variantMetafields, 'overview') ||
                        product.description || '';
        const patentStatus = this._getMetafieldValue(variantMetafields, 'patented') || '';

        // Generate slides based on Swiper availability
        const usesSwiper = typeof Swiper !== 'undefined';
        const imageSlides = usesSwiper ?
            this._generateSwiperSlides(images, product.title, videos) :
            this._generateSimpleSlides(images, product.title);
        const thumbnails = hasMultipleMedia ?
            (usesSwiper ? this._generateSwiperThumbs(images, product.title, videos) :
            this._generateSimpleThumbs(images, product.title)) : '';

        // Generate content sections
        const overviewContent = this._generateOverviewContent(overview);
        const featuresContent = this._generateFeaturesContent(variantMetafields);
        const specsContent = this._generateSpecsContent(variantMetafields);
        const priceHTML = this._generatePriceHTML(variant, product.handle);

        // Availability badge (Storm color)
        const availabilityBadge = !available ? `
            <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium tb-out-of-stock-badge ml-2">
                Out of Stock
            </span>
        ` : '';

        // Desktop HTML
        const desktopHTML = `
            <button type="button" class="modal-close absolute top-4 right-4 z-10 text-gray-400 hover:text-gray-600 bg-white rounded-full p-2 shadow-md transition-colors duration-200">
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>

            <div class="image-container w-full flex-shrink-0 bg-gray-50
                        h-64 sm:h-72 md:h-80 lg:h-96 xl:h-[28rem] 2xl:h-[32rem]
                        min-h-[16rem] max-h-[45vh]">
                ${usesSwiper ? `
                    <div class="swiper preview-main-gallery w-full h-full">
                        <div class="swiper-wrapper">${imageSlides}</div>
                        ${hasMultipleMedia ? `
                            <div class="swiper-button-next preview-button-next"></div>
                            <div class="swiper-button-prev preview-button-prev"></div>
                        ` : ''}
                    </div>
                ` : imageSlides}
            </div>

            ${hasMultipleMedia ? `
                <div class="p-2 md:p-3 lg:p-4 flex-shrink-0 bg-gray-50">
                    ${usesSwiper ? `
                        <div class="swiper preview-thumbnails-gallery ${hasMoreThanFive ? 'carousel-mode' : ''}">
                            <div class="swiper-wrapper">${thumbnails}</div>
                            ${hasMoreThanFive ? `
                                <div class="swiper-button-next thumbnail-button-next"></div>
                                <div class="swiper-button-prev thumbnail-button-prev"></div>
                            ` : ''}
                        </div>
                    ` : `<div class="grid grid-cols-5 gap-2">${thumbnails}</div>`}
                </div>
            ` : ''}

            <div class="p-4 md:p-5 lg:p-6 overflow-y-auto flex-grow min-h-[200px]">
                ${patentStatus ? `<div class="mb-3"><span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium tb-patent-badge">${patentStatus}</span></div>` : ''}
                <h3 class="text-base md:text-lg font-medium font-heading leading-tight text-gray-900">
                    ${product.title}
                    ${availabilityBadge}
                </h3>
                <div class="text-sm text-gray-600 mt-2 space-y-1">
                    <div class="flex items-center">
                        <span class="font-medium">SKU:</span>
                        <span class="ml-1 font-light">${variant.sku || product.handle.toUpperCase()}</span>
                    </div>
                    ${variant.barcode ? `<div class="flex items-center">
                        <span class="font-medium">UPC:</span>
                        <span class="ml-1 font-light">${variant.barcode}</span>
                    </div>` : ''}
                    ${priceHTML}
                </div>
                ${overviewContent}
                ${featuresContent}
                ${specsContent}
            </div>
        `;

        // Mobile HTML
        const mobileHTML = `
            <div class="drag-handle-area w-full py-3 flex justify-center cursor-grab bg-white">
                <div class="drag-handle w-12 h-1 bg-gray-300 rounded-full"></div>
            </div>
            <div class="overflow-y-auto" style="height: calc(90vh - 60px); max-height: calc(90vh - 60px);">
                <div class="image-container w-full aspect-square bg-gray-50">
                    ${usesSwiper ? `
                        <div class="swiper preview-main-gallery-mobile w-full h-full">
                            <div class="swiper-wrapper">${imageSlides}</div>
                            ${hasMultipleMedia ? `
                                <div class="swiper-pagination preview-pagination-mobile"></div>
                            ` : ''}
                        </div>
                    ` : imageSlides}
                </div>
                ${hasMultipleMedia ? `
                    <div class="p-2 bg-gray-50">
                        ${usesSwiper ? `
                            <div class="swiper preview-thumbnails-gallery-mobile">
                                <div class="swiper-wrapper">${thumbnails}</div>
                            </div>
                        ` : `<div class="flex gap-2 overflow-x-auto">${thumbnails}</div>`}
                    </div>
                ` : ''}
                <div class="p-4">
                    ${patentStatus ? `<div class="mb-3"><span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium tb-patent-badge">${patentStatus}</span></div>` : ''}
                    <h3 class="text-lg font-bold font-heading leading-tight text-gray-900">
                        ${product.title}
                        ${availabilityBadge}
                    </h3>
                    <div class="text-sm text-gray-600 mt-2 space-y-1">
                        <div class="flex items-center">
                            <span class="font-medium">SKU:</span>
                            <span class="ml-1 font-light">${variant.sku || product.handle.toUpperCase()}</span>
                        </div>
                        ${variant.barcode ? `<div class="flex items-center">
                            <span class="font-medium">UPC:</span>
                            <span class="ml-1 font-light">${variant.barcode}</span>
                        </div>` : ''}
                        ${priceHTML}
                    </div>
                    ${overviewContent}
                    ${featuresContent}
                    ${specsContent}
                    <div class="pb-5"></div>
                </div>
            </div>
        `;

        return `
            <div class="fixed inset-0 z-[60] tough-bar-preview-modal opacity-0 pointer-events-none">
                <div class="absolute inset-0 preview-modal-backdrop bg-black/60 opacity-0" data-backdrop="true"></div>

                <!-- Desktop Modal -->
                <div class="hidden md:flex items-center justify-center min-h-screen p-4">
                    <div class="preview-modal-content relative bg-white
                                w-full max-w-xl lg:max-w-2xl xl:max-w-3xl 2xl:max-w-4xl
                                mx-auto rounded-lg shadow-2xl overflow-hidden flex flex-col
                                max-h-[90vh] opacity-0 scale-95">
                        ${desktopHTML}
                    </div>
                </div>

                <!-- Mobile Modal -->
                <div class="block md:hidden">
                    <div class="mobile-preview-content fixed inset-x-0 bottom-0 opacity-0 translate-y-full">
                        <div class="bg-white rounded-t-3xl shadow-xl w-full max-h-[90vh] overflow-hidden">
                            ${mobileHTML}
                        </div>
                    </div>
                </div>

                <style>
                    @media (max-height: 768px) {
                        .tough-bar-preview-modal .image-container {
                            max-height: 40vh !important;
                        }
                    }

                    @media (min-height: 1080px) {
                        .tough-bar-preview-modal .image-container {
                            max-height: 500px !important;
                        }
                    }

                    .tough-bar-preview-modal .preview-modal-content {
                        display: flex !important;
                        flex-direction: column !important;
                    }

                    .tough-bar-preview-modal .image-container {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    .tough-bar-preview-modal .image-container img {
                        max-width: 100%;
                        max-height: 100%;
                        object-fit: contain;
                    }
                </style>
            </div>
        `;
    }

    /**
     * Generate Swiper slides (images + videos)
     */
    _generateSwiperSlides(images, title, videos = []) {
        let slides = '';

        // Image slides
        if (images.length) {
            slides = images.map(img => `
                <div class="swiper-slide">
                    <img src="${this._getSizedImageUrl(img, 500)}"
                         alt="${title}"
                         class="w-full h-full object-contain" />
                </div>
            `).join('');
        }

        // Video slides (appended after images)
        if (videos.length) {
            slides += videos.map(video => `
                <div class="swiper-slide tb-video-slide" data-video-id="${video.id}">
                    <div class="tb-video-container">
                        <img src="${video.thumbUrl}"
                             alt="${title} video"
                             class="tb-video-poster" />
                        <button type="button" class="tb-video-play-btn" data-embed-url="${video.embedUrl}">
                            <svg class="tb-play-icon" viewBox="0 0 24 24" fill="white">
                                <path d="M8 5v14l11-7z"/>
                            </svg>
                        </button>
                    </div>
                </div>
            `).join('');
        }

        if (!slides) {
            return `<div class="swiper-slide"><div class="w-full h-full flex items-center justify-center text-gray-400">No image available</div></div>`;
        }

        return slides;
    }

    /**
     * Generate Swiper thumbnails (images + videos)
     */
    _generateSwiperThumbs(images, title, videos = []) {
        // Image thumbnails
        let thumbs = images.map((img, index) => `
            <div class="swiper-slide cursor-pointer" style="width: 60px; height: 60px;">
                <div class="w-full h-full bg-gray-50 rounded overflow-hidden border-2 border-transparent hover:border-gray-300 transition-colors">
                    <img src="${this._getSizedImageUrl(img, 80)}"
                         alt="${title} thumbnail"
                         class="w-full h-full object-contain" />
                </div>
            </div>
        `).join('');

        // Video thumbnails (appended after images)
        if (videos.length) {
            thumbs += videos.map(video => `
                <div class="swiper-slide cursor-pointer" style="width: 60px; height: 60px;">
                    <div class="w-full h-full bg-gray-50 rounded overflow-hidden border-2 border-transparent hover:border-gray-300 transition-colors relative">
                        <img src="${video.thumbUrl}"
                             alt="${title} video thumbnail"
                             class="w-full h-full object-cover" />
                        <div class="tb-thumb-play-overlay">
                            <svg class="tb-thumb-play-icon" viewBox="0 0 24 24" fill="white">
                                <path d="M8 5v14l11-7z"/>
                            </svg>
                        </div>
                    </div>
                </div>
            `).join('');
        }

        return thumbs;
    }

    /**
     * Generate simple slides (fallback)
     */
    _generateSimpleSlides(images, title) {
        if (!images.length) {
            return '<div class="text-gray-400 text-center">No image available</div>';
        }
        return images.map((img, index) => `
            <img src="${this._getSizedImageUrl(img, 500)}"
                 alt="${title}"
                 class="gallery-image w-full h-full object-contain ${index > 0 ? 'hidden' : ''}"
            />
        `).join('');
    }

    /**
     * Generate simple thumbnails (fallback)
     */
    _generateSimpleThumbs(images, title) {
        return images.map((img, index) => `
            <div class="gallery-thumb cursor-pointer border-2 ${index === 0 ? 'tb-border-active' : 'border-transparent'}
                        rounded overflow-hidden transition-colors">
                <img src="${this._getSizedImageUrl(img, 80)}"
                     alt="${title} thumbnail"
                     class="w-full h-full object-contain"
                />
            </div>
        `).join('');
    }

    /**
     * Get sized image URL
     */
    _getSizedImageUrl(url, size) {
        if (!url || !url.includes('cdn.shopify.com')) return url;
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}width=${size}`;
    }

    /**
     * Show loading state on element
     */
    _showElementLoading(element, isLoading) {
        if (!element) return;

        if (isLoading) {
            element.classList.add('pointer-events-none', 'opacity-50');
            element.style.cursor = 'wait';
        } else {
            element.classList.remove('pointer-events-none', 'opacity-50');
            element.style.cursor = '';
        }
    }

    /**
     * Lock body scroll
     */
    _lockScroll() {
        this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

        document.body.style.cssText = `
            position: fixed !important;
            top: -${this.scrollPosition}px !important;
            left: 0 !important;
            right: 0 !important;
            width: 100% !important;
            overflow: hidden !important;
            padding-right: ${scrollbarWidth}px !important;
        `;

        document.documentElement.style.overflow = 'hidden';
        document.body.setAttribute('data-scroll-locked', 'true');
    }

    /**
     * Unlock body scroll
     */
    _unlockScroll() {
        document.body.removeAttribute('data-scroll-locked');
        document.body.style.cssText = '';
        document.documentElement.style.overflow = '';

        if (this.scrollPosition !== undefined) {
            window.scrollTo(0, this.scrollPosition);
            this.scrollPosition = undefined;
        }
    }

    /**
     * Add required styles
     */
    _addStyles() {
        if (document.getElementById('tough-bar-preview-modal-styles')) return;

        const style = document.createElement('style');
        style.id = 'tough-bar-preview-modal-styles';
        style.textContent = `
            .tough-bar-preview-modal {
                transition: opacity 0.3s ease;
            }

            .tough-bar-preview-modal .preview-modal-backdrop {
                transition: opacity 0.3s ease;
            }

            .tough-bar-preview-modal .preview-modal-content {
                transition: opacity 0.3s ease, transform 0.3s ease;
            }

            .tough-bar-preview-modal .mobile-preview-content {
                transition: transform 0.3s ease-out, opacity 0.3s ease-out;
                touch-action: none;
                overscroll-behavior: contain;
            }

            .tough-bar-preview-modal .drag-handle-area {
                user-select: none;
                -webkit-user-select: none;
                touch-action: pan-x pan-down pinch-zoom;
            }

            .tough-bar-preview-modal .drag-handle {
                background-color: #cbd5e0 !important;
                width: 48px !important;
                height: 4px !important;
                border-radius: 9999px !important;
            }

            /* Border colors using CSS variables */
            .tb-border-active {
                border-color: var(--tb-border-active) !important;
            }

            /* Patent badge styling */
            .tb-patent-badge {
                background-color: var(--tb-primary-light);
                color: var(--tb-primary-text);
            }

            /* Out of stock badge styling (Storm color) */
            .tb-out-of-stock-badge {
                background-color: var(--tb-primary-light);
                color: var(--tb-primary);
            }

            /* Features marker styling (Storm color) */
            .tb-features-marker {
                color: var(--tb-primary);
            }

            /* Price styling */
            .tb-price-compare {
                text-decoration: line-through;
                text-decoration-color: var(--tb-danger);
                text-decoration-thickness: 2px;
                color: #6B7280;
            }

            .tb-price-sale {
                color: var(--tb-danger);
            }

            /* Product link styling */
            .tb-product-link {
                color: var(--tb-primary);
                transition: color 0.2s ease;
            }

            .tb-product-link:hover {
                color: var(--tb-primary-hover);
                text-decoration: underline;
            }

            .tb-product-link svg {
                transition: transform 0.2s ease;
            }

            .tb-product-link:hover svg {
                transform: translateX(2px);
            }

            /* See more/less buttons styling */
            .tb-see-more-btn,
            .tb-see-less-btn {
                color: var(--tb-primary);
                font-weight: 500;
                transition: color 0.2s;
                background: none;
                border: none;
                padding: 0;
                cursor: pointer;
            }

            .tb-see-more-btn:hover,
            .tb-see-less-btn:hover {
                color: var(--tb-primary-hover);
                text-decoration: underline;
            }

            /* Specs content styling */
            .tough-bar-preview-modal .specs-content {
                animation: tbSlideDown 0.3s ease-out;
            }

            .tough-bar-preview-modal .specs-content.hidden {
                display: none;
            }

            @keyframes tbSlideDown {
                from {
                    opacity: 0;
                    transform: translateY(-10px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }

            .tough-bar-preview-modal .specs-toggle-icon {
                transition: transform 0.2s ease;
            }

            .tough-bar-preview-modal .specs-toggle-icon.rotate-45 {
                transform: rotate(45deg);
            }

            /* Gallery thumbnail styles */
            .tough-bar-preview-modal .gallery-thumb {
                width: 60px;
                height: 60px;
            }

            /* Clickable image styling */
            .tb-preview-trigger {
                cursor: pointer;
                transition: opacity 0.2s ease;
            }

            .tb-preview-trigger:hover {
                opacity: 0.8;
            }

            /* Video slide styles */
            .tb-video-slide {
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .tb-video-container {
                position: relative;
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                background: #000;
            }

            .tb-video-poster {
                width: 100%;
                height: 100%;
                object-fit: contain;
            }

            .tb-video-play-btn {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 72px;
                height: 72px;
                background: rgba(0, 0, 0, 0.6);
                border: none;
                border-radius: 50%;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: background 0.2s ease, transform 0.2s ease;
            }

            .tb-video-play-btn:hover {
                background: rgba(0, 0, 0, 0.8);
                transform: translate(-50%, -50%) scale(1.1);
            }

            .tb-play-icon {
                width: 32px;
                height: 32px;
                margin-left: 4px; /* Optical center adjustment */
            }

            .tb-video-iframe {
                width: 100%;
                height: 100%;
                border: none;
            }

            /* Video thumbnail play overlay */
            .tb-thumb-play-overlay {
                position: absolute;
                inset: 0;
                background: rgba(0, 0, 0, 0.4);
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .tb-thumb-play-icon {
                width: 20px;
                height: 20px;
            }

            /* Mobile-specific overrides */
            @media (max-width: 768px) {
                .tough-bar-preview-modal .mobile-preview-content {
                    touch-action: none;
                    overscroll-behavior: contain;
                    transition: transform 0.3s ease-out, opacity 0.3s ease-out;
                }

                .tough-bar-preview-modal .drag-handle-area {
                    cursor: grab;
                    user-select: none;
                    -webkit-user-select: none;
                    touch-action: pan-x pan-down pinch-zoom;
                }

                .tb-see-more-btn,
                .tb-see-less-btn {
                    color: var(--tb-primary);
                    font-weight: 500;
                    background: none;
                    border: none;
                    padding: 2px 4px;
                    cursor: pointer;
                    touch-action: manipulation;
                }

                /* Mobile video adjustments */
                .tb-video-play-btn {
                    width: 56px;
                    height: 56px;
                }

                .tb-play-icon {
                    width: 24px;
                    height: 24px;
                }
            }
        `;
        document.head.appendChild(style);
    }

    /**
     * Add Swiper-specific styles
     */
    _addSwiperStyles() {
        if (document.getElementById('tough-bar-swiper-styles')) return;

        const style = document.createElement('style');
        style.id = 'tough-bar-swiper-styles';
        style.textContent = `
            /* Swiper Gallery Styles */
            .tough-bar-preview-modal .preview-main-gallery,
            .tough-bar-preview-modal .preview-main-gallery-mobile {
                width: 100%;
                height: 100%;
            }

            .tough-bar-preview-modal img {
                -webkit-user-select: none;
                -moz-user-select: none;
                -ms-user-select: none;
                user-select: none;
                -webkit-user-drag: none;
                pointer-events: auto;
            }

            .tough-bar-preview-modal .swiper-slide {
                -webkit-user-select: none;
                -moz-user-select: none;
                -ms-user-select: none;
                user-select: none;
            }

            .tough-bar-preview-modal .preview-thumbnails-gallery,
            .tough-bar-preview-modal .preview-thumbnails-gallery-mobile {
                height: 70px;
                padding: 10px 0;
                position: relative;
                min-height: 70px;
                opacity: 0;
                transition: opacity 0.2s 0.1s ease-in-out;
            }

            .tough-bar-preview-modal .preview-thumbnails-gallery.swiper-initialized,
            .tough-bar-preview-modal .preview-thumbnails-gallery-mobile.swiper-initialized {
                opacity: 1;
            }

            .tough-bar-preview-modal .preview-thumbnails-gallery .swiper-slide,
            .tough-bar-preview-modal .preview-thumbnails-gallery-mobile .swiper-slide {
                width: 60px !important;
                height: 60px;
                margin: 0 4px;
            }

            .tough-bar-preview-modal .preview-thumbnails-gallery.carousel-mode {
                padding: 0 35px;
            }

            .tough-bar-preview-modal .preview-thumbnails-gallery-mobile.carousel-mode {
                padding: 0 30px;
            }

            .tough-bar-preview-modal .preview-thumbnails-gallery .swiper-slide div,
            .tough-bar-preview-modal .preview-thumbnails-gallery-mobile .swiper-slide div {
                height: 100%;
                width: 100%;
                border: 2px solid transparent;
                transition: border-color 0.3s ease;
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: hidden;
                border-radius: 4px;
            }

            .tough-bar-preview-modal .preview-thumbnails-gallery .swiper-slide-thumb-active div,
            .tough-bar-preview-modal .preview-thumbnails-gallery-mobile .swiper-slide-thumb-active div {
                border-color: var(--tb-border-active) !important;
            }

            /* Swiper Navigation */
            .tough-bar-preview-modal .preview-button-next,
            .tough-bar-preview-modal .preview-button-prev {
                color: var(--tb-primary);
                background: white;
                border-radius: 50%;
                box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                width: 40px;
                height: 40px;
            }

            .tough-bar-preview-modal .preview-button-next:after,
            .tough-bar-preview-modal .preview-button-prev:after {
                font-size: 16px;
                font-weight: bold;
            }

            .tough-bar-preview-modal .thumbnail-button-next,
            .tough-bar-preview-modal .thumbnail-button-prev {
                color: var(--tb-primary);
                background: white;
                border-radius: 50%;
                box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                width: 24px;
                height: 24px;
                margin-top: 0;
                top: 50%;
                transform: translateY(-50%);
                z-index: 10;
            }

            .tough-bar-preview-modal .thumbnail-button-next:after,
            .tough-bar-preview-modal .thumbnail-button-prev:after {
                font-size: 10px;
                font-weight: bold;
            }

            .tough-bar-preview-modal .thumbnail-button-next {
                right: 5px;
            }

            .tough-bar-preview-modal .thumbnail-button-prev {
                left: 5px;
            }

            /* Swiper Pagination */
            .tough-bar-preview-modal .preview-pagination-mobile {
                position: relative !important;
                bottom: auto !important;
                margin-top: 10px !important;
            }

            .tough-bar-preview-modal .preview-pagination-mobile .swiper-pagination-bullet {
                background-color: var(--tb-bullet-inactive);
                opacity: 1;
            }

            .tough-bar-preview-modal .preview-pagination-mobile .swiper-pagination-bullet-active {
                background-color: var(--tb-bullet-active);
            }

            /* Responsive thumbnails */
            @media (max-height: 768px) {
                .tough-bar-preview-modal .preview-thumbnails-gallery {
                    height: 50px !important;
                }

                .tough-bar-preview-modal .preview-thumbnails-gallery .swiper-slide {
                    width: 50px !important;
                    height: 50px !important;
                }
            }
        `;
        document.head.appendChild(style);
    }
}

// Export for use in tough-bar-builder.js
window.ToughBarPreviewModal = ToughBarPreviewModal;
